import { BetaFeatureKey } from '@/lib/beta-features'
import queryClient from '@/lib/queryClient'
import type { OrganizationId, OrganizationIdInput } from '@/lib/organization-scoping'
import { organizationQueryKeys } from '@/lib/services/organizations/organizations.query'
import { snapdragonQueryKeys } from '@/lib/services/snapdragon/snapdragon.query'
import { queryOptions, useMutation, useQuery } from '@tanstack/react-query'
import { getOrganizationBetaFeatures, updateOrganizationBetaFeature } from './beta-features.api'

interface BetaFeatureQueryRoot {
  entity: 'beta-features'
}

/** Beta features are managed by global users per organization, outside tenant-scoped keys. */
export type BetaFeatureOrganizationQueryKey = [
  BetaFeatureQueryRoot & { scope: 'organization'; organizationId: number },
]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

const betaFeatureQueryRoot: BetaFeatureQueryRoot = { entity: 'beta-features' }

export const betaFeatureQueryKeys = {
  all: [betaFeatureQueryRoot] satisfies [BetaFeatureQueryRoot],
  organization: (organizationId: OrganizationIdInput): BetaFeatureOrganizationQueryKey => [
    {
      ...betaFeatureQueryKeys.all[0],
      scope: 'organization',
      organizationId: Number(organizationId),
    },
  ],
}

export const betaFeatureQueries = {
  organization: (organizationId: OrganizationIdInput) =>
    queryOptions({
      queryKey: betaFeatureQueryKeys.organization(organizationId),
      queryFn: getOrganizationBetaFeatures,
    }),
}

/** The query stays disabled until there is an organization id. */
export function useOrganizationBetaFeatures(
  organizationId: OrganizationIdInput,
  params: QueryParams<ReturnType<typeof betaFeatureQueries.organization>> = {}
) {
  return useQuery({
    ...betaFeatureQueries.organization(organizationId),
    enabled: Boolean(organizationId),
    ...params,
  })
}

export function invalidateOrganizationBetaFeatureQueries({
  organizationId,
  featureKeys = [],
}: {
  organizationId: OrganizationId
  featureKeys?: string[]
}) {
  const invalidations = [
    queryClient.invalidateQueries({
      queryKey: betaFeatureQueryKeys.organization(organizationId),
    }),
    queryClient.invalidateQueries({
      queryKey: organizationQueryKeys.all,
    }),
  ]

  if (featureKeys.includes(BetaFeatureKey.Snapdragon)) {
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: snapdragonQueryKeys.all({ organizationId }),
      })
    )
  }

  return Promise.all(invalidations)
}

export function useUpdateOrganizationBetaFeatureMutation({
  invalidateOnSuccess = true,
}: { invalidateOnSuccess?: boolean } = {}) {
  return useMutation({
    mutationFn: updateOrganizationBetaFeature,
    onSuccess: (_, { organizationId, featureKey }) => {
      if (!invalidateOnSuccess) {
        return
      }

      return invalidateOrganizationBetaFeatureQueries({
        organizationId,
        featureKeys: [featureKey],
      })
    },
  })
}
